import { useEffect, useRef } from "react";
import { useStytch, useStytchUser } from "../utils/useStytch";
import { useApiClient } from "@src/packages/api/louise-api";
import {
  usePatient,
  useUpdatePatient,
} from "@src/packages/patients/hooks/usePatient";
import { normalizeCountryCode } from "@src/packages/location/countryPreference";
import { useDetectedCountry } from "@src/packages/location/useDetectedCountry";
import { loadOnboardingData } from "../onboarding/onboardingStorage";
import { waitForPatientClaim } from "./patientClaim";

/**
 * Every signed-in profile gets a country.
 *
 * Onboarding asks for it on the Location step, but an account created from
 * the Login screen (OAuth, phone OTP) never passes through onboarding, and a
 * patient can clear their address later. Features gate and bias on the
 * profile country, so once a session exists and the profile has none, the
 * detected one — the same chain the Location step pre-selects from — is
 * written to the profile. It is a default, not an answer: the Location step
 * shows it pre-selected, and Personal info lets her change it.
 *
 * Runs once per session and defers to a pending onboarding save, which
 * carries the country she actually chose.
 */
export function CountryDefaultBoot() {
  const stytch = useStytch();
  const { user } = useStytchUser();
  const { patientId } = useApiClient();
  const { data: patient, isSuccess } = usePatient();
  const { mutate: updatePatient } = useUpdatePatient();
  const claimRequestedRef = useRef(false);
  const attemptedRef = useRef(false);

  // A brand-new account has no patient claim yet, and the patient query
  // stays disabled until it lands. Ask for it the way the onboarding rescue
  // does; the query enables itself once the Stytch user carries the claim.
  useEffect(() => {
    if (!user || patientId || claimRequestedRef.current) return;
    claimRequestedRef.current = true;
    void waitForPatientClaim(stytch);
  }, [user, patientId, stytch]);

  const address = patient?.addresses?.[0];
  // Any stored value is hers, even a legacy spelling this app cannot read.
  const needsCountry = Boolean(
    user && isSuccess && patient && !address?.country?.trim(),
  );
  const { countryCode, source, isDetecting } = useDetectedCountry({
    enabled: needsCountry,
  });

  useEffect(() => {
    if (!needsCountry || isDetecting || attemptedRef.current) return;
    attemptedRef.current = true;

    const saveDefaultCountry = async () => {
      const stored = await loadOnboardingData();
      if (normalizeCountryCode(stored?.country)) return;

      updatePatient(
        {
          addresses: [
            {
              ...(address?.id ? { id: address.id } : {}),
              ...(address
                ? {
                    address1: address.address1 ?? null,
                    address2: address.address2 ?? null,
                    city: address.city ?? null,
                    postalCode: address.postalCode ?? null,
                  }
                : {}),
              country: countryCode,
            },
          ],
        },
        {
          onSuccess: () => {
            console.info(
              `[CountryDefaultBoot] Saved default country ${countryCode} (${source}).`,
            );
          },
          onError: (error) => {
            console.warn(
              "[CountryDefaultBoot] Could not save the default country:",
              error,
            );
          },
        },
      );
    };

    void saveDefaultCountry();
  }, [needsCountry, isDetecting, countryCode, source, address, updatePatient]);

  return null;
}
